const escapeHtml = (value = '') =>
    String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const normalizeWebsite = (value = '') => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) {
        return { href: '', text: '' };
    }

    if (/^https?:\/\//i.test(trimmed)) {
        return { href: trimmed, text: trimmed };
    }

    return {
        href: `https://${trimmed.replace(/^\/+/, '')}`,
        text: trimmed
    };
};

const defaultFieldConfig = {
    enabled: true,
    optional: false
};

export default (config = {}) => ({
    config,
    texts: config.texts || {},
    form: {
        name: '',
        title: '',
        addressLine1: config.defaults?.addressLine1 ?? '',
        addressLine2: config.defaults?.addressLine2 ?? '',
        companyLine: config.defaults?.companyLine ?? '',
        mobile: '',
        phone: '',
        email: '',
        website: config.defaults?.website ?? ''
    },
    visibility: {},
    copyState: 'idle',
    copyMessage: '',
    init() {
        Object.keys(this.config.fields || {}).forEach((key) => {
            this.visibility[key] = this.isEnabled(key);
        });
    },
    field(key) {
        return {
            ...defaultFieldConfig,
            ...(this.config.fields?.[key] || {})
        };
    },
    isEnabled(key) {
        return this.field(key).enabled !== false;
    },
    isOptional(key) {
        return this.isEnabled(key) && this.field(key).optional === true;
    },
    isVisible(key) {
        return this.isEnabled(key) && (!this.isOptional(key) || this.visibility[key] !== false);
    },
    value(key) {
        return String(this.form[key] ?? '').trim();
    },
    hasRenderableContent() {
        return Boolean(this.config.logoUrl)
            || this.signatureRows().length > 0
            || (this.isVisible('name') && this.value('name'))
            || (this.isVisible('title') && this.value('title'));
    },
    signatureRows() {
        const website = normalizeWebsite(this.value('website'));
        const email = this.value('email');

        const rows = [
            { key: 'addressLine1', value: this.value('addressLine1') },
            { key: 'addressLine2', value: this.value('addressLine2') },
            { key: 'companyLine', value: this.value('companyLine') },
            { key: 'mobile', value: this.value('mobile'), prefix: this.config.labels?.mobile ?? '' },
            { key: 'phone', value: this.value('phone'), prefix: this.config.labels?.phone ?? '' },
            { key: 'email', value: email, prefix: this.config.labels?.email ?? '', href: email ? `mailto:${email}` : '' },
            { key: 'website', value: website.text, prefix: this.config.labels?.website ?? '', href: website.href }
        ];

        return rows.filter((row) => this.isVisible(row.key) && row.value);
    },
    buildSignatureHtml() {
        if (!this.hasRenderableContent()) {
            return '';
        }

        const name = this.isVisible('name') ? this.value('name') : '';
        const title = this.isVisible('title') ? this.value('title') : '';
        const rows = this.signatureRows();
        const hasTopSection = Boolean(name || title);
        const detailRows = rows
            .map((row) => {
                const prefix = row.prefix ? `${escapeHtml(row.prefix)} ` : '';
                const content = row.href
                    ? `<a href="${escapeHtml(row.href)}" style="color:#003C28;text-decoration:underline;">${escapeHtml(row.value)}</a>`
                    : escapeHtml(row.value);

                return `<tr><td style="padding:0 0 4px; font-family:Arial, Helvetica, sans-serif; font-size:13px; line-height:18px; color:#003C28;">${prefix}${content}</td></tr>`;
            })
            .join('');

        const logoCell = this.config.logoUrl
            ? `<td style="padding:0 20px 0 0; vertical-align:top;"><img src="${escapeHtml(this.config.logoUrl)}" alt="${escapeHtml(this.config.logoAlt || '')}" style="display:block; border:0; outline:none; text-decoration:none; max-width:180px; height:auto;"></td>`
            : '';

        const spacerRow = hasTopSection && detailRows
            ? '<tr><td style="height:10px; line-height:10px; font-size:10px;">&nbsp;</td></tr>'
            : '';

        return `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse; font-family:Arial, Helvetica, sans-serif; color:#003C28;"><tr>${logoCell}<td style="padding:0; vertical-align:top;"><table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;">${name ? `<tr><td style="padding:0 0 2px; font-family:Arial, Helvetica, sans-serif; font-size:18px; line-height:22px; font-weight:700; color:#003C28;">${escapeHtml(name)}</td></tr>` : ''}${title ? `<tr><td style="padding:0; font-family:Arial, Helvetica, sans-serif; font-size:14px; line-height:18px; color:#003C28;">${escapeHtml(title)}</td></tr>` : ''}${spacerRow}${detailRows}</table></td></tr></table>`;
    },
    buildSignatureText() {
        const lines = [];
        const name = this.isVisible('name') ? this.value('name') : '';
        const title = this.isVisible('title') ? this.value('title') : '';

        if (name) {
            lines.push(name);
        }

        if (title) {
            lines.push(title);
        }

        this.signatureRows().forEach((row) => {
            lines.push(row.prefix ? `${row.prefix} ${row.value}` : row.value);
        });

        return lines.join('\n');
    },
    async copySignature() {
        const html = this.buildSignatureHtml();
        if (!html) {
            this.setCopyState('error', this.texts.nothingToCopy || '');
            return;
        }

        const text = this.buildSignatureText();

        try {
            if (navigator.clipboard?.write && window.ClipboardItem) {
                await navigator.clipboard.write([
                    new ClipboardItem({
                        'text/html': new Blob([html], { type: 'text/html' }),
                        'text/plain': new Blob([text], { type: 'text/plain' })
                    })
                ]);
            } else if (!(await this.fallbackCopy(html, text))) {
                throw new Error('Clipboard fallback failed');
            }

            this.setCopyState('success', this.texts.copySuccess || '');
        } catch (error) {
            console.error(error);
            this.setCopyState('error', this.texts.copyError || '');
        }
    },
    async fallbackCopy(html, text) {
        const copyContainer = document.createElement('div');
        copyContainer.setAttribute('contenteditable', 'true');
        copyContainer.style.position = 'fixed';
        copyContainer.style.opacity = '0';
        copyContainer.style.pointerEvents = 'none';
        copyContainer.style.whiteSpace = 'pre-wrap';
        copyContainer.style.left = '-9999px';
        copyContainer.innerHTML = html;
        document.body.appendChild(copyContainer);

        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(copyContainer);
        selection?.removeAllRanges();
        selection?.addRange(range);

        let copied = false;

        try {
            copied = document.execCommand('copy');
        } catch {
            copied = false;
        }

        selection?.removeAllRanges();
        copyContainer.remove();

        if (copied) {
            return true;
        }

        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }

        return false;
    },
    setCopyState(state, message) {
        this.copyState = state;
        this.copyMessage = message;

        window.clearTimeout(this.copyTimeout);
        this.copyTimeout = window.setTimeout(() => {
            this.copyState = 'idle';
            this.copyMessage = '';
        }, 3000);
    }
});
